import { useState, useEffect } from 'react';

interface GeolocationState {
  loading: boolean;
  error: string | null;
  position: {
    latitude: number;
    longitude: number;
  } | null;
  permissionStatus: 'granted' | 'denied' | 'prompt' | 'unknown';
}

export function useGeolocation() {
  const [state, setState] = useState<GeolocationState>({
    loading: false,
    error: null,
    position: null,
    permissionStatus: 'unknown',
  });

  const requestGeolocation = async () => {
    if (!navigator.geolocation) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Geolocation is not supported by your browser',
      }));
      return false;
    }

    setState(prev => ({ ...prev, loading: true }));

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      setState({
        loading: false,
        error: null,
        position: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        },
        permissionStatus: 'granted',
      });
      return true;
    } catch (error: any) {
      let errorMessage = 'Failed to get your location';
      let permissionStatus: GeolocationState['permissionStatus'] = 'unknown';

      if (error.code === error.PERMISSION_DENIED) {
        errorMessage = 'Permission to access location was denied';
        permissionStatus = 'denied';
      } else if (error.code === error.POSITION_UNAVAILABLE) {
        errorMessage = 'Location information is unavailable';
      } else if (error.code === error.TIMEOUT) {
        errorMessage = 'The request to get location timed out';
      }

      setState({
        loading: false,
        error: errorMessage,
        position: null,
        permissionStatus,
      });
      return false;
    }
  };

  useEffect(() => {
    // Check if the browser supports the permissions API
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'geolocation' }).then((result) => {
        const permissionStatus = result.state as GeolocationState['permissionStatus'];
        
        setState(prev => ({
          ...prev,
          permissionStatus,
        }));

        // Listen for permission changes
        result.addEventListener('change', () => {
          setState(prev => ({
            ...prev,
            permissionStatus: result.state as GeolocationState['permissionStatus'],
          }));
        });
      });
    }
  }, []);

  return {
    ...state,
    requestGeolocation,
  };
}
